import React from "react";
import { useTranslation } from "react-i18next";
import { LeaderboardEntry, Num } from "@uno/protocol";
import Avatar from "component/Ui/Avatar";
import LevelBadge from "component/Ui/LevelBadge";
import getPublicAssetUrl from "utils/PublicAsset";

const ME_SHADOW = "0 0 0 3px rgba(251,191,36,0.95), 0 0 24px rgba(251,191,36,0.45)";

type Props = {
    entry: LeaderboardEntry;
    isMe: boolean;
};

type RankTheme = {
    paper: string;
    text: string;
    textWeak: string;
    numberOutline: string;
    numberDeep: string;
    dividerColor: string;
    shadow: string;
};

const RANK_THEMES: Record<number, RankTheme> = {
    1: {
        paper: "#fef3c7",
        text: "text-amber-900",
        textWeak: "text-amber-900/40",
        numberOutline: "#b45309",
        numberDeep: "#78350f",
        dividerColor: "rgba(180,83,9,0.18)",
        shadow: "0 10px 24px rgba(251,191,36,0.35)",
    },
    2: {
        paper: "#e2e8f0",
        text: "text-slate-800",
        textWeak: "text-slate-800/40",
        numberOutline: "#475569",
        numberDeep: "#1e293b",
        dividerColor: "rgba(71,85,105,0.18)",
        shadow: "0 8px 20px rgba(148,163,184,0.3)",
    },
    3: {
        paper: "#fed7aa",
        text: "text-orange-900",
        textWeak: "text-orange-900/40",
        numberOutline: "#9a3412",
        numberDeep: "#431407",
        dividerColor: "rgba(154,52,18,0.18)",
        shadow: "0 8px 20px rgba(251,146,60,0.3)",
    },
};

const buildRankShadow = (theme: RankTheme) => {
    const { numberOutline: o, numberDeep: d } = theme;
    return `-1.2px -1.2px 0 ${o}, 1.2px -1.2px 0 ${o}, -1.2px 1.2px 0 ${o}, 1.2px 1.2px 0 ${o}, 0 2px 0 ${d}, 0 3px 5px rgba(0,0,0,0.35)`;
};

const PodiumCard: React.FC<Props> = ({ entry, isMe }) => {
    const { t } = useTranslation();
    const theme = RANK_THEMES[entry.rank];

    if(!theme) {
        return null;
    }

    const score = entry.totalScore;
    const isFirst = entry.rank === 1;

    return (
        <div
            className={
                isFirst
                    ? "relative w-full pt-[22px] md:pt-[26px]"
                    : "relative w-full pt-[20px] md:pt-[26px]"
            }
        >
            <div
                className="relative flex flex-col w-full rounded-2xl md:rounded-3xl overflow-hidden border-[6px] md:border-[7px] border-white aspect-[3/4]"
                style={{
                    background: theme.paper,
                    boxShadow: isMe
                        ? `${theme.shadow}, ${ME_SHADOW}`
                        : theme.shadow,
                }}
            >
                <span
                    className={
                        isFirst
                            ? "absolute top-0.5 md:top-1 left-1.5 md:left-2 z-20 font-changa-one text-base md:text-xl leading-none text-white"
                            : "absolute top-0.5 md:top-1 left-1.5 md:left-2 z-20 font-changa-one text-sm md:text-xl leading-none text-white"
                    }
                    style={{
                        transform: "skewX(-10deg)",
                        transformOrigin: "left top",
                        textShadow: buildRankShadow(theme),
                    }}
                >
                    {entry.rank}
                </span>

                {entry.flag && (
                    <img
                        src={getPublicAssetUrl(`assets/flags/${entry.flag}.svg`)}
                        alt=""
                        className="absolute top-1.5 right-1.5 md:top-2 md:right-2 z-20 w-3.5 h-2.5 md:w-4 md:h-3 object-contain rounded-sm shadow-[0_1px_2px_rgba(0,0,0,0.25)]"
                        draggable={false}
                    />
                )}

                <div
                    className={
                        isFirst
                            ? "mx-1.5 md:mx-2 px-1.5 pt-[28px] sm:pt-[30px] md:pt-[32px] pb-1 flex items-center justify-center shrink-0"
                            : "mx-1.5 md:mx-2 px-1.5 pt-[24px] sm:pt-[28px] md:pt-[32px] pb-1 flex items-center justify-center shrink-0"
                    }
                    style={{ borderBottom: `2px dotted ${theme.dividerColor}` }}
                >
                    <span className={`font-extrabold w-full min-w-0 text-[11px] leading-tight text-center break-all line-clamp-2 ${theme.text}`}>
                        {entry.username}
                    </span>
                </div>

                <div className="flex-1 flex flex-col items-center justify-center px-1 md:px-2">
                    <span
                        className={
                            isFirst
                                ? `font-black tabular-nums leading-none ${theme.text} text-[20px] sm:text-[24px] md:text-[28px]`
                                : `font-black tabular-nums leading-none ${theme.text} text-[16px] sm:text-[19px] md:text-[22px]`
                        }
                        style={{ textShadow: `0 2px 0 ${theme.dividerColor}` }}
                    >
                        {Num.compact(score)}
                    </span>
                    <span className={`text-[9px] uppercase tracking-wider font-extrabold mt-0.5 md:mt-1 ${theme.textWeak}`}>
                        {t("leaderboard.points")}
                    </span>
                </div>

            </div>

            <div className="absolute left-1/2 top-0 -translate-x-1/2 z-30">
                <div className="relative">
                    <Avatar
                        src={entry.avatar}
                        className={`${isFirst ? "w-11 h-11 md:w-12 md:h-12" : "w-10 h-10 md:w-11 md:h-11"} ${isMe ? "ring-2 ring-amber-300 ring-offset-2 ring-offset-transparent rounded-full" : ""}`}
                    />
                    <LevelBadge level={entry.level} className="-bottom-0.5 right-0 w-4 h-4 text-[8px]" />
                    {isMe && (
                        <span
                            className="absolute bottom-0 left-1/2 whitespace-nowrap text-[7px] uppercase tracking-wider font-extrabold text-amber-950 bg-amber-300 rounded-full px-1.5 py-0.5 shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
                            style={{ transform: "translate(-50%, calc(50% - 1px))" }}
                        >
                            {t("leaderboard.you")}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PodiumCard;
